import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["show", "defaultShow", "popoverClassName", "onHide", "onShow", "onVisibleChange", "refType", "refId", "triggerIds", "unionPopoverIds", "placement", "offset", "preventOverflow", "boundariesDetectionDebounce", "overflowBoundaries", "preventAroundOverflow", "arrow", "arrowOffset", "popoverId", "singleGroupId", "captureOptions", "zIndex", "lazyInit", "getPopper", "getContainer"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * @file withPopperContent
 * @author Yu Zong(liuxuanzy@qq.com)
 */
import React, { useMemo, useRef } from 'react';
import { getZIndex } from '@tencent/glue-dom-helper';
import { LayerProvider } from '@tencent/glue-react-layer';
import { usePopover } from '@tencent/glue-react-popper';
import { useUncontrolledShow, useRefFunction } from '@tencent/glue-react-use';
import { Portal } from '../../units/Portal'; // eslint-disable-next-line max-len

export function withPopperContent(Component) {
  var RenderComponent = Component;

  function PopperContent(props) {
    var show = props.show,
        defaultShow = props.defaultShow,
        popoverClassName = props.popoverClassName,
        onHide = props.onHide,
        onShow = props.onShow,
        onVisibleChange = props.onVisibleChange,
        refType = props.refType,
        refId = props.refId,
        triggerIds = props.triggerIds,
        unionPopoverIds = props.unionPopoverIds,
        propPlacement = props.placement,
        offset = props.offset,
        preventOverflow = props.preventOverflow,
        boundariesDetectionDebounce = props.boundariesDetectionDebounce,
        overflowBoundaries = props.overflowBoundaries,
        preventAroundOverflow = props.preventAroundOverflow,
        arrow = props.arrow,
        arrowOffset = props.arrowOffset,
        popoverId = props.popoverId,
        singleGroupId = props.singleGroupId,
        captureOptions = props.captureOptions,
        zIndex = props.zIndex,
        _props$lazyInit = props.lazyInit,
        lazyInit = _props$lazyInit === void 0 ? false : _props$lazyInit,
        getPopper = props.getPopper,
        getCustomerContainer = props.getContainer,
        extra = _objectWithoutProperties(props, _excluded);

    var _useUncontrolledShow = useUncontrolledShow({
      show: show,
      onVisibleChange: onVisibleChange,
      onShow: onShow,
      onHide: onHide,
      defaultShow: defaultShow
    }),
        _useUncontrolledShow2 = _slicedToArray(_useUncontrolledShow, 2),
        popperShow = _useUncontrolledShow2[0],
        onPopperShowChange = _useUncontrolledShow2[1];

    var arrowRef = useRef(null);

    var _usePopover = usePopover({
      show: popperShow,
      onVisibleChange: onPopperShowChange,
      arrowRef: arrowRef,
      popoverId: popoverId,
      singleGroupId: singleGroupId,
      captureOptions: captureOptions,
      refType: refType,
      refId: refId,
      triggerIds: triggerIds,
      unionPopoverIds: unionPopoverIds,
      placement: propPlacement,
      offset: offset,
      preventOverflow: preventOverflow,
      boundariesDetectionDebounce: boundariesDetectionDebounce,
      overflowBoundaries: overflowBoundaries,
      preventAroundOverflow: preventAroundOverflow,
      arrow: arrow,
      arrowOffset: arrowOffset
    }),
        _usePopover2 = _slicedToArray(_usePopover, 2),
        popoverRef = _usePopover2[0],
        _usePopover2$ = _usePopover2[1],
        data = _usePopover2$.data,
        placement = _usePopover2$.placement,
        popoverStyle = _usePopover2$.popoverStyle,
        arrowInfo = _usePopover2$.arrowInfo,
        getContainer = _usePopover2$.getContainer,
        onBlurCapture = _usePopover2$.onBlurCapture,
        onMouseLeave = _usePopover2$.onMouseLeave,
        onMouseEnter = _usePopover2$.onMouseEnter,
        onFocusCapture = _usePopover2$.onFocusCapture;

    var getContainerHandler = useRefFunction(function () {
      return (getCustomerContainer === null || getCustomerContainer === void 0 ? void 0 : getCustomerContainer()) || getContainer();
    });
    var mountRef = useRef(show || !lazyInit);
    var zIndexRef = useRef(0);
    useMemo(function () {
      if (popperShow) {
        mountRef.current = true;
        zIndexRef.current = getZIndex();
      }
    }, [popperShow]);

    if (!mountRef.current) {
      return null;
    }

    return React.createElement(Portal, {
      getContainer: getContainerHandler
    }, React.createElement(LayerProvider, {
      value: getContainerHandler()
    }, React.createElement("div", {
      ref: popoverRef,
      onBlurCapture: onBlurCapture,
      onMouseLeave: onMouseLeave,
      onMouseEnter: onMouseEnter,
      onFocusCapture: onFocusCapture,
      className: popoverClassName,
      style: _objectSpread(_objectSpread({}, popoverStyle), {}, {
        zIndex: zIndex || zIndexRef.current,
        display: popperShow ? '' : 'none'
      })
    }, React.createElement(RenderComponent, _objectSpread(_objectSpread(_objectSpread({}, extra), arrowInfo), {}, {
      placement: placement,
      showArrow: arrow,
      arrowRef: arrowRef
    }), typeof getPopper === 'function' ? getPopper(data) : getPopper))));
  }

  return PopperContent;
}